import React, { useEffect } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { usePermissions } from "../context/PermissionsContext";

/**
 * Login page.  Compayr no longer accepts an email/password here - users
 * authenticate via Microsoft Entra ID SSO.  In LOCAL dev mode the backend's
 * /api/auth/login endpoint auto-grants a session, so the same button flow
 * works locally.
 */
const LoginPage: React.FC = () => {
  const { authenticated, loading } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  // If we arrived here but already have a session, bounce to the app.
  useEffect(() => {
    if (!loading && authenticated) {
      const redirectTo =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ||
        "/dashboard";
      navigate(redirectTo, { replace: true });
    }
  }, [authenticated, loading, navigate, location.state]);

  const handleSignIn = () => {
    // Full-page navigation (not fetch) so the browser follows the 302 from
    // /api/auth/login to Microsoft and back.
    window.location.href = "/api/auth/login";
  };

  const ssoFailed = new URLSearchParams(location.search).get("error") === "sso_failed";

  return (
    <Box className="login-main">
      <Paper
        elevation={6}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          bgcolor: "#0f1116",
          color: "#fff",
          textAlign: "center",
          borderRadius: 3,
          margin: 4,
          height: "fit-content",
        }}
      >
        <Paper
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            bgcolor: "#0f1116",
            color: "#fff",
            boxShadow: "none",
            minWidth: 320,
          }}
        >
          <img src="/payassist-logo.svg" className="payassist-logo" alt="Compayr Logo" />
          <Typography variant="caption" sx={{ mb: 3, fontFamily: "Inter" }}>
            Sign in with your Deloitte Microsoft account
          </Typography>

          {ssoFailed && (
            <Typography variant="caption" color="error" sx={{ mb: 2, fontFamily: "Inter" }}>
              Single sign-on failed. Please try again or contact your administrator.
            </Typography>
          )}

          <Button
            onClick={handleSignIn}
            variant="contained"
            fullWidth
            sx={{
              mt: 4,
              mb: 2,
              fontFamily: "Inter",
              textTransform: "none",
              fontSize: "16px",
              backgroundColor: "#007CB0",
            }}
            disabled={loading}
          >
            Sign in with Microsoft
          </Button>

          <Typography variant="caption" sx={{ color: "#fff", mt: 2, fontFamily: "Inter" }}>
            by <img src="/deloitte-white-logo.png" className="del-logo" alt="Deloitte Logo" />
          </Typography>
        </Paper>
      </Paper>
    </Box>
  );
};

export default LoginPage;
